(() => {
  if (window.__goodnessTeaCartBridgeV11Loaded) {
    return;
  }

  window.__goodnessTeaCartBridgeV11Loaded = true;

  const QUANTITY_UNLOCK_FALLBACK = 6000;
  const ADD_LOADING_FALLBACK = 12000;
  const PUB_SUB_RETRY_LIMIT = 50;

  let cartErrorUnsubscriber = null;
  let pubSubRetryCount = 0;

  /* ======================================================
     GENERAL HELPERS
     ====================================================== */

  function getCartURL() {
    return (
      window.routes?.cart_url ||
      `${window.Shopify?.routes?.root || '/'}cart`
    );
  }

  function getCartPageSections() {
    const items =
      document.getElementById(
        'main-cart-items'
      );

    const footer =
      document.getElementById(
        'main-cart-footer'
      );

    const recommendations =
      document.getElementById(
        'main-cart-recommendations'
      );

    return [
      {
        domId: 'main-cart-items',
        sectionId: items?.dataset.id,
      },
      {
        domId: 'main-cart-footer',
        sectionId: footer?.dataset.id,
      },
      {
        domId: 'main-cart-recommendations',
        sectionId:
          recommendations?.dataset.id,
      },
    ].filter((section) => {
      return Boolean(
        section.sectionId &&
        document.getElementById(
          section.domId
        )
      );
    });
  }

  function removeDuplicateSections(
    sections,
    keyBuilder
  ) {
    const seen = new Set();

    return sections.filter(
      (section) => {
        const key =
          keyBuilder(section);

        if (
          !key ||
          seen.has(key)
        ) {
          return false;
        }

        seen.add(key);

        return true;
      }
    );
  }

  function getDrawerQuantityInput(
    drawer,
    line
  ) {
    return (
      drawer?.querySelector(
        `#Drawer-quantity-${line}`
      ) ||
      drawer?.querySelector(
        `.quantity__input[data-index="${line}"]`
      ) ||
      drawer?.querySelector(
        `input[name="updates[]"][data-index="${line}"]`
      )
    );
  }

  function isQuantityLimitMessage(
    message
  ) {
    const text =
      String(message || '')
        .trim()
        .toLowerCase();

    if (!text) {
      return false;
    }

    return (
      (
        text.includes('only add') &&
        text.includes('of this item')
      ) ||
      (
        text.includes('maximum') &&
        text.includes('quantity')
      ) ||
      (
        text.includes('quantity') &&
        text.includes('available')
      )
    );
  }

  function getDrawerScrollElement(
    drawer
  ) {
    return (
      drawer?.querySelector(
        '.gt-cart-drawer__scroll'
      ) ||
      drawer?.querySelector(
        '.drawer__contents'
      ) ||
      drawer?.querySelector(
        'cart-drawer-items'
      )
    );
  }

  /* ======================================================
     ADD-TO-CART LOADING SCREEN
     ====================================================== */

  function ensureAddLoadingStyles() {
    if (
      document.getElementById(
        'GT-CartAddLoadingStyles'
      )
    ) {
      return;
    }

    const style =
      document.createElement('style');

    style.id =
      'GT-CartAddLoadingStyles';

    style.textContent = `
      cart-drawer .drawer__inner {
        position: relative;
      }

      .gt-cart-add-loading__overlay {
        position: absolute;
        inset: 0;
        z-index: 2147482000;
        display: none;
        align-items: center;
        justify-content: center;
        min-height: 100%;
        padding: 32px;
        color: #353027;
        background: #ffffff;
        text-align: center;
      }

      cart-drawer.gt-cart-add-loading
        .gt-cart-add-loading__overlay {
        display: flex;
      }

      cart-drawer.gt-cart-add-loading
        .drawer__inner
        > :not(.gt-cart-add-loading__overlay) {
        visibility: hidden;
      }

      .gt-cart-add-loading__content {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 17px;
        width: 100%;
        max-width: 310px;
      }

      .gt-cart-add-loading__spinner {
        display: block;
        width: 44px;
        height: 44px;
        border: 4px solid rgba(29, 86, 55, 0.18);
        border-top-color: #1d5637;
        border-radius: 50%;
        animation: gt-cart-add-loading-spin 0.75s linear infinite;
      }

      .gt-cart-add-loading__heading {
        margin: 0;
        color: #353027;
        font-family: var(--font-body-family), Arial, sans-serif;
        font-size: 21px;
        font-weight: 700;
        line-height: 1.25;
      }

      .gt-cart-add-loading__text {
        margin: -7px 0 0;
        color: #66625d;
        font-family: var(--font-body-family), Arial, sans-serif;
        font-size: 15px;
        line-height: 1.4;
      }

      @keyframes gt-cart-add-loading-spin {
        to {
          transform: rotate(360deg);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .gt-cart-add-loading__spinner {
          animation-duration: 1.5s;
        }
      }
    `;

    document.head.appendChild(style);
  }

  function getAddLoadingOverlay(
    drawer
  ) {
    const drawerInner =
      drawer?.querySelector(
        '.drawer__inner'
      );

    if (!drawerInner) {
      return null;
    }

    let overlay =
      drawerInner.querySelector(
        '.gt-cart-add-loading__overlay'
      );

    if (overlay) {
      return overlay;
    }

    overlay =
      document.createElement('div');

    overlay.className =
      'gt-cart-add-loading__overlay';

    overlay.setAttribute(
      'role',
      'status'
    );

    overlay.setAttribute(
      'aria-live',
      'polite'
    );

    overlay.setAttribute(
      'aria-label',
      'Adding the product to your cart'
    );

    overlay.innerHTML = `
      <div class="gt-cart-add-loading__content">
        <span
          class="gt-cart-add-loading__spinner"
          aria-hidden="true"
        ></span>

        <p class="gt-cart-add-loading__heading">
          Adding to your cart…
        </p>

        <p class="gt-cart-add-loading__text">
          Just a moment while we update your order.
        </p>
      </div>
    `;

    drawerInner.appendChild(overlay);

    return overlay;
  }

  function showAddLoading(
    drawer,
    triggeredBy
  ) {
    if (!drawer) {
      return;
    }

    ensureAddLoadingStyles();
    getAddLoadingOverlay(drawer);

    window.clearTimeout(
      drawer.__gtAddLoadingTimer
    );

    drawer.__gtAddRequestPending =
      true;

    drawer.__gtWasEmptyBeforeAdd =
      drawer.classList.contains(
        'is-empty'
      );

    /*
     * Prevent Dawn's empty-cart layout from controlling focus
     * and visibility while the loading screen is active.
     */
    drawer.classList.remove(
      'is-empty'
    );

    drawer.classList.add(
      'gt-cart-add-loading'
    );

    /*
     * The patched open() method recognises the pending add
     * request and opens immediately rather than fetching old
     * cart markup first.
     */
    drawer.open?.(
      triggeredBy ||
      document.activeElement
    );

    drawer.__gtAddLoadingTimer =
      window.setTimeout(() => {
        hideAddLoading(
          drawer,
          true
        );
      }, ADD_LOADING_FALLBACK);
  }

  function hideAddLoading(
    drawer,
    restoreEmptyState = false
  ) {
    if (!drawer) {
      return;
    }

    window.clearTimeout(
      drawer.__gtAddLoadingTimer
    );

    drawer.classList.remove(
      'gt-cart-add-loading'
    );

    drawer.__gtAddRequestPending =
      false;

    if (
      restoreEmptyState &&
      drawer.__gtWasEmptyBeforeAdd
    ) {
      drawer.classList.add(
        'is-empty'
      );
    }

    drawer.__gtWasEmptyBeforeAdd =
      false;

    const overlay =
      drawer.querySelector(
        '.gt-cart-add-loading__overlay'
      );

    if (overlay) {
      overlay.setAttribute(
        'aria-hidden',
        'true'
      );
    }
  }

  function isAddToCartForm(form) {
    if (
      !(form instanceof HTMLFormElement)
    ) {
      return false;
    }

    if (
      form.closest('cart-drawer') ||
      form.closest('cart-items')
    ) {
      return false;
    }

    return Boolean(
      form.closest('product-form') ||
      form.dataset.type ===
        'add-to-cart-form' ||
      form.matches(
        '[data-type="add-to-cart-form"]'
      )
    );
  }

  function handleAddToCartSubmit(
    event
  ) {
    const form = event.target;

    if (!isAddToCartForm(form)) {
      return;
    }

    const submitButton =
      event.submitter ||
      form.querySelector(
        '[type="submit"]'
      );

    if (
      submitButton?.disabled ||
      submitButton?.getAttribute(
        'aria-disabled'
      ) === 'true'
    ) {
      return;
    }

    const drawer =
      document.querySelector(
        'cart-drawer'
      );

    if (!drawer) {
      return;
    }

    showAddLoading(
      drawer,
      submitButton
    );
  }

  function bindCartErrorListener() {
    const pubSubAvailable =
      typeof subscribe ===
        'function' &&
      typeof PUB_SUB_EVENTS !==
        'undefined' &&
      Boolean(
        PUB_SUB_EVENTS.cartError
      );

    if (pubSubAvailable) {
      cartErrorUnsubscriber =
        subscribe(
          PUB_SUB_EVENTS.cartError,
          () => {
            const drawer =
              document.querySelector(
                'cart-drawer'
              );

            hideAddLoading(
              drawer,
              true
            );
          }
        );

      return;
    }

    pubSubRetryCount += 1;

    if (
      pubSubRetryCount >=
      PUB_SUB_RETRY_LIMIT
    ) {
      return;
    }

    window.setTimeout(
      bindCartErrorListener,
      100
    );
  }

  /* ======================================================
     QUANTITY BUTTON LOCK
     ====================================================== */

  function getDrawerQuantityButtons(
    drawerItems
  ) {
    return drawerItems
      ? drawerItems.querySelectorAll(
          'quantity-input .quantity__button'
        )
      : [];
  }

  function visuallyLockQuantityButtons(
    drawerItems
  ) {
    getDrawerQuantityButtons(
      drawerItems
    ).forEach((button) => {
      button.disabled = true;

      button.setAttribute(
        'aria-disabled',
        'true'
      );
    });
  }

  function unlockDrawerQuantityControls(
    drawerItems
  ) {
    if (!drawerItems) {
      return;
    }

    drawerItems.__gtQuantityClickLocked =
      false;

    window.clearTimeout(
      drawerItems
        .__gtQuantityUnlockTimer
    );

    drawerItems.removeAttribute(
      'data-quantity-update-pending'
    );

    getDrawerQuantityButtons(
      drawerItems
    ).forEach((button) => {
      button.disabled = false;

      button.removeAttribute(
        'aria-disabled'
      );
    });
  }

  function lockDrawerQuantityControls(
    drawerItems,
    deferButtonDisable = false
  ) {
    if (!drawerItems) {
      return;
    }

    drawerItems.__gtQuantityClickLocked =
      true;

    drawerItems.setAttribute(
      'data-quantity-update-pending',
      'true'
    );

    window.clearTimeout(
      drawerItems
        .__gtQuantityUnlockTimer
    );

    drawerItems
      .__gtQuantityUnlockTimer =
      window.setTimeout(() => {
        unlockDrawerQuantityControls(
          drawerItems
        );
      }, QUANTITY_UNLOCK_FALLBACK);

    if (deferButtonDisable) {
      /*
       * Let Dawn finish processing the accepted click before
       * disabling the controls.
       */
      queueMicrotask(() => {
        if (
          drawerItems
            .__gtQuantityClickLocked
        ) {
          visuallyLockQuantityButtons(
            drawerItems
          );
        }
      });

      return;
    }

    visuallyLockQuantityButtons(
      drawerItems
    );
  }

  /* ======================================================
     MINUS TO ZERO = REMOVE ITEM
     ====================================================== */

  function removeItemFromMinusButton(
    event,
    button,
    drawerItems,
    quantityInput
  ) {
    const currentQuantity =
      Number.parseInt(
        quantityInput.value,
        10
      );

    const quantityStep =
      Math.max(
        1,
        Number.parseInt(
          quantityInput.step,
          10
        ) || 1
      );

    const nextQuantity =
      currentQuantity -
      quantityStep;

    if (
      button.name !== 'minus' ||
      !Number.isFinite(
        currentQuantity
      ) ||
      nextQuantity > 0
    ) {
      return false;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    lockDrawerQuantityControls(
      drawerItems
    );

    const line =
      quantityInput.dataset.index;

    const variantId =
      quantityInput.dataset
        .quantityVariantId;

    try {
      /*
       * Dawn's own CartItems method treats quantity zero as a
       * removal, exactly like the bin icon.
       */
      drawerItems.updateQuantity(
        line,
        0,
        event,
        'minus',
        variantId
      );
    } catch (error) {
      unlockDrawerQuantityControls(
        drawerItems
      );

      throw error;
    }

    return true;
  }

  function handleDrawerQuantityClick(
    event
  ) {
    if (
      !(event.target instanceof Element)
    ) {
      return;
    }

    const button =
      event.target.closest(
        'cart-drawer quantity-input .quantity__button'
      );

    if (!button) {
      return;
    }

    const drawerItems =
      button.closest(
        'cart-drawer-items'
      );

    const quantityInput =
      button
        .closest('quantity-input')
        ?.querySelector(
          '.quantity__input'
        );

    if (
      !drawerItems ||
      !quantityInput
    ) {
      return;
    }

    /*
     * Ignore extra presses while Shopify is processing the
     * accepted quantity change.
     */
    if (
      drawerItems
        .__gtQuantityClickLocked
    ) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      return;
    }

    /*
     * Pressing minus at the lowest quantity removes the line
     * through Dawn's existing quantity-zero removal path.
     */
    if (
      removeItemFromMinusButton(
        event,
        button,
        drawerItems,
        quantityInput
      )
    ) {
      return;
    }

    /*
     * Accept one normal plus/minus click, then temporarily
     * lock every drawer quantity button.
     */
    lockDrawerQuantityControls(
      drawerItems,
      true
    );
  }

  /* ======================================================
     REFRESH DRAWER MARKUP BEFORE OPENING FROM CART PAGE
     ====================================================== */

  function bindRefreshedDrawerControls(
    drawer
  ) {
    if (!drawer) {
      return;
    }

    const overlay =
      drawer.querySelector(
        '#CartDrawer-Overlay'
      ) ||
      drawer.querySelector(
        '.drawer__overlay'
      );

    if (
      overlay &&
      !overlay.dataset
        .gtBridgeCloseBound
    ) {
      overlay.dataset
        .gtBridgeCloseBound = 'true';

      overlay.addEventListener(
        'click',
        () => {
          drawer.close?.();
        }
      );
    }

    const closeButton =
      drawer.querySelector(
        '[data-cart-drawer-close]'
      ) ||
      drawer.querySelector(
        '.drawer__close'
      );

    if (
      closeButton &&
      !closeButton.dataset
        .gtBridgeCloseBound
    ) {
      closeButton.dataset
        .gtBridgeCloseBound = 'true';

      closeButton.addEventListener(
        'click',
        (event) => {
          event.preventDefault();

          drawer.close?.();
        }
      );
    }
  }

  async function refreshDrawerMarkup(
    drawer
  ) {
    const currentRoot =
      drawer?.querySelector(
        '#CartDrawer'
      );

    if (
      !drawer ||
      !currentRoot
    ) {
      return;
    }

    const drawerWasOpen =
      drawer.classList.contains(
        'active'
      );

    const oldScrollElement =
      getDrawerScrollElement(
        drawer
      );

    const savedScrollTop =
      oldScrollElement?.scrollTop || 0;

    drawer.recommendationsRequest?.abort();

    drawer.recommendationsRequest =
      null;

    drawer.recommendationsKey = '';

    const response =
      await fetch(
        `${getCartURL()}?section_id=cart-drawer&gt_refresh=${Date.now()}`,
        {
          method: 'GET',
          cache: 'no-store',
          credentials: 'same-origin',

          headers: {
            Accept: 'text/html',
            'X-Requested-With':
              'XMLHttpRequest',
          },
        }
      );

    if (!response.ok) {
      throw new Error(
        `Cart drawer refresh returned ${response.status}.`
      );
    }

    const responseText =
      await response.text();

    const parsedDocument =
      new DOMParser().parseFromString(
        responseText,
        'text/html'
      );

    const refreshedDrawer =
      parsedDocument.querySelector(
        'cart-drawer'
      );

    const refreshedRoot =
      parsedDocument.querySelector(
        '#CartDrawer'
      );

    if (!refreshedRoot) {
      throw new Error(
        'The refreshed CartDrawer markup was not found.'
      );
    }

    currentRoot.innerHTML =
      refreshedRoot.innerHTML;

    currentRoot.className =
      refreshedRoot.className;

    if (refreshedDrawer) {
      drawer.classList.toggle(
        'is-empty',
        refreshedDrawer.classList.contains(
          'is-empty'
        )
      );
    }

    drawer.pendingUIState = null;

    bindRefreshedDrawerControls(
      drawer
    );

    const newScrollElement =
      getDrawerScrollElement(
        drawer
      );

    if (newScrollElement) {
      newScrollElement.scrollTop =
        savedScrollTop;
    }

    if (drawerWasOpen) {
      drawer.classList.add(
        'active'
      );
    }

    if (
      typeof drawer.scheduleSync ===
      'function'
    ) {
      drawer.scheduleSync();
    }
  }

  /* ======================================================
     PATCH CART DRAWER
     ====================================================== */

  async function patchCartDrawer() {
    await customElements.whenDefined(
      'cart-drawer'
    );

    const CartDrawerClass =
      customElements.get(
        'cart-drawer'
      );

    if (
      !CartDrawerClass ||
      CartDrawerClass.prototype
        .__gtBridgeV11Patched
    ) {
      return;
    }

    const originalGetSections =
      CartDrawerClass.prototype
        .getSectionsToRender;

    /*
     * Drawer actions also request the cart-page sections when
     * the customer happens to be on /cart.
     */
    CartDrawerClass.prototype
      .getSectionsToRender =
      function () {
        const originalSections =
          typeof originalGetSections ===
          'function'
            ? originalGetSections.call(
                this
              )
            : [];

        const pageSections =
          getCartPageSections().map(
            (section) => ({
              id: section.sectionId,

              selector:
                `#${section.domId} .js-contents`,
            })
          );

        return removeDuplicateSections(
          [
            ...originalSections,
            ...pageSections,
          ],
          (section) => {
            return [
              section.id || '',
              section.selector || '',
            ].join('|');
          }
        );
      };

    const originalRenderContents =
      CartDrawerClass.prototype
        .renderContents;

    /*
     * Shopify has now returned the updated cart sections.
     * Render them first, then remove the loading screen.
     */
    if (
      typeof originalRenderContents ===
      'function'
    ) {
      CartDrawerClass.prototype
        .renderContents =
        function (parsedState) {
          const result =
            originalRenderContents.call(
              this,
              parsedState
            );

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              hideAddLoading(
                this,
                false
              );

              this.classList.remove(
                'is-empty'
              );

              if (
                typeof this.scheduleSync ===
                'function'
              ) {
                this.scheduleSync();
              }
            });
          });

          return result;
        };
    }

    const originalOpen =
      CartDrawerClass.prototype.open;

    /*
     * Refresh before an ordinary cart-icon opening on /cart,
     * preserving the existing cart-page-to-drawer sync.
     *
     * During an add-to-cart request, open immediately with the
     * loader instead of fetching stale pre-add markup.
     */
    CartDrawerClass.prototype.open =
      function (triggeredBy) {
        if (
          this.__gtAddRequestPending ||
          this.classList.contains(
            'gt-cart-add-loading'
          )
        ) {
          return originalOpen.call(
            this,
            triggeredBy
          );
        }

        if (
          !document.getElementById(
            'main-cart-items'
          ) ||
          this.classList.contains(
            'active'
          )
        ) {
          return originalOpen.call(
            this,
            triggeredBy
          );
        }

        if (triggeredBy) {
          this.__gtPendingOpenTrigger =
            triggeredBy;
        }

        if (
          this.__gtOpenRefreshPromise
        ) {
          return this
            .__gtOpenRefreshPromise;
        }

        this.__gtOpenRefreshPromise =
          refreshDrawerMarkup(this)
            .catch((error) => {
              console.error(
                'The cart drawer could not be refreshed before opening:',
                error
              );
            })
            .finally(() => {
              const trigger =
                this
                  .__gtPendingOpenTrigger ||
                triggeredBy;

              this.__gtPendingOpenTrigger =
                null;

              this.__gtOpenRefreshPromise =
                null;

              originalOpen.call(
                this,
                trigger
              );
            });

        return this
          .__gtOpenRefreshPromise;
      };

    CartDrawerClass.prototype
      .__gtBridgeV11Patched = true;
  }

  /* ======================================================
     PATCH CART DRAWER ITEMS
     ====================================================== */

  async function patchCartDrawerItems() {
    await customElements.whenDefined(
      'cart-drawer-items'
    );

    const CartDrawerItemsClass =
      customElements.get(
        'cart-drawer-items'
      );

    if (
      !CartDrawerItemsClass ||
      CartDrawerItemsClass.prototype
        .__gtBridgeV11Patched
    ) {
      return;
    }

    const originalGetSections =
      CartDrawerItemsClass.prototype
        .getSectionsToRender;

    /*
     * Quantity changes and removals in the drawer also refresh
     * the cart page when it exists behind the drawer.
     */
    CartDrawerItemsClass.prototype
      .getSectionsToRender =
      function () {
        const originalSections =
          typeof originalGetSections ===
          'function'
            ? originalGetSections.call(
                this
              )
            : [];

        const pageSections =
          getCartPageSections().map(
            (section) => ({
              id: section.domId,
              section:
                section.sectionId,
              selector:
                '.js-contents',
            })
          );

        return removeDuplicateSections(
          [
            ...originalSections,
            ...pageSections,
          ],
          (section) => {
            return [
              section.id || '',
              section.section || '',
              section.selector || '',
            ].join('|');
          }
        );
      };

    const originalUpdateQuantity =
      CartDrawerItemsClass.prototype
        .updateQuantity;

    /*
     * Record the requested amount so a false quantity-limit
     * warning can be suppressed after Shopify accepts it.
     */
    if (
      typeof originalUpdateQuantity ===
      'function'
    ) {
      CartDrawerItemsClass.prototype
        .updateQuantity =
        function (
          line,
          quantity,
          event,
          name,
          variantId
        ) {
          const drawer =
            this.closest(
              'cart-drawer'
            ) ||
            document.querySelector(
              'cart-drawer'
            );

          const input =
            getDrawerQuantityInput(
              drawer,
              line
            );

          const previousQuantity =
            Number.parseInt(
              input?.value,
              10
            );

          const requestedQuantity =
            Number.parseInt(
              quantity,
              10
            );

          this.__gtLastQuantityRequest = {
            line: String(line),

            previous:
              Number.isFinite(
                previousQuantity
              )
                ? previousQuantity
                : null,

            requested:
              Number.isFinite(
                requestedQuantity
              )
                ? requestedQuantity
                : null,

            createdAt: Date.now(),
          };

          if (
            !this
              .__gtQuantityClickLocked
          ) {
            lockDrawerQuantityControls(
              this
            );
          }

          return originalUpdateQuantity.call(
            this,
            line,
            quantity,
            event,
            name,
            variantId
          );
        };
    }

    const originalDisableLoading =
      CartDrawerItemsClass.prototype
        .disableLoading;

    /*
     * Re-enable the quantity controls as soon as Dawn finishes
     * the current cart request.
     */
    if (
      typeof originalDisableLoading ===
      'function'
    ) {
      CartDrawerItemsClass.prototype
        .disableLoading =
        function (line) {
          const result =
            originalDisableLoading.call(
              this,
              line
            );

          unlockDrawerQuantityControls(
            this
          );

          return result;
        };
    }

    /*
     * Keep line-specific feedback inside the drawer and remove
     * false stock-limit messages when the requested quantity
     * is already present in the confirmed drawer HTML.
     */
    CartDrawerItemsClass.prototype
      .updateLiveRegions =
      function (line, message) {
        const drawer =
          this.closest(
            'cart-drawer'
          ) ||
          document.querySelector(
            'cart-drawer'
          );

        const request =
          this.__gtLastQuantityRequest;

        const currentInput =
          getDrawerQuantityInput(
            drawer,
            line
          );

        const currentQuantity =
          Number.parseInt(
            currentInput?.value,
            10
          );

        let finalMessage =
          String(message || '').trim();

        const requestMatchesLine =
          request &&
          request.line === String(line);

        const requestIsRecent =
          requestMatchesLine &&
          Date.now() -
            request.createdAt <
            10000;

        const requestedQuantityAccepted =
          requestIsRecent &&
          Number.isFinite(
            request.requested
          ) &&
          Number.isFinite(
            currentQuantity
          ) &&
          currentQuantity ===
            request.requested;

        if (
          requestedQuantityAccepted &&
          isQuantityLimitMessage(
            finalMessage
          )
        ) {
          finalMessage = '';
        }

        /*
         * Drawer-originated messages must not appear in a cart
         * page line item behind the drawer.
         */
        const pageLineError =
          document.getElementById(
            `Line-item-error-${line}`
          );

        const pageErrorText =
          pageLineError?.querySelector(
            '.cart-item__error-text'
          );

        if (pageErrorText) {
          pageErrorText.textContent = '';
        }

        const drawerLineError =
          drawer?.querySelector(
            `#CartDrawer-LineItemError-${line}`
          );

        const drawerErrorText =
          drawerLineError?.querySelector(
            '.cart-item__error-text'
          );

        if (drawerErrorText) {
          drawerErrorText.textContent =
            finalMessage;
        }

        if (drawerLineError) {
          drawerLineError.hidden =
            finalMessage === '';
        }

        const drawerLineStatus =
          drawer?.querySelector(
            '#CartDrawer-LineItemStatus'
          );

        if (drawerLineStatus) {
          drawerLineStatus.setAttribute(
            'aria-hidden',
            'true'
          );
        }

        const drawerLiveRegion =
          drawer?.querySelector(
            '#CartDrawer-LiveRegionText'
          );

        if (drawerLiveRegion) {
          drawerLiveRegion.setAttribute(
            'aria-hidden',
            'false'
          );

          window.setTimeout(() => {
            drawerLiveRegion.setAttribute(
              'aria-hidden',
              'true'
            );
          }, 1000);
        }

        if (requestMatchesLine) {
          this.__gtLastQuantityRequest =
            null;
        }
      };

    CartDrawerItemsClass.prototype
      .__gtBridgeV11Patched = true;
  }

  /* ======================================================
     INITIALISE
     ====================================================== */

  async function initialise() {
    try {
      ensureAddLoadingStyles();

      await Promise.all([
        patchCartDrawer(),
        patchCartDrawerItems(),
      ]);

      /*
       * Capture before Dawn's quantity-input handler so rapid
       * presses can be ignored and minus-to-zero can be routed
       * directly through the removal path.
       */
      document.addEventListener(
        'click',
        handleDrawerQuantityClick,
        true
      );

      /*
       * Open the drawer immediately with a proper loading
       * screen as soon as a valid product form is submitted.
       */
      document.addEventListener(
        'submit',
        handleAddToCartSubmit,
        true
      );

      bindCartErrorListener();
    } catch (error) {
      console.error(
        'Goodness Tea cart synchronisation could not initialise:',
        error
      );
    }
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initialise,
      {
        once: true,
      }
    );
  } else {
    initialise();
  }

  window.addEventListener(
    'pagehide',
    () => {
      const drawer =
        document.querySelector(
          'cart-drawer'
        );

      window.clearTimeout(
        drawer?.__gtAddLoadingTimer
      );

      if (
        typeof cartErrorUnsubscriber ===
        'function'
      ) {
        cartErrorUnsubscriber();
      }

      document.removeEventListener(
        'click',
        handleDrawerQuantityClick,
        true
      );

      document.removeEventListener(
        'submit',
        handleAddToCartSubmit,
        true
      );
    },
    {
      once: true,
    }
  );
})();